import type {
  MatrixFocusTarget,
  UiEffectContextForKey,
  UiEffectRegistry,
  UiEffectRegistryKey,
} from './types';
import { historyAction as historyActions } from 'flow/service/history/constants';
import type { HistoryAction } from 'flow/service/history/constants';
import type {
  ExpressionUiEffectContext,
  WorkflowHistoryEntry,
} from 'flow/store/redux/types';
import {
  undoUiEffectMap,
} from './constants';
import type { DataRoute, Threshold } from 'flow/store/engine/types';
import type { Axis } from 'flow/editor/components/NodeEditorDrawer/MatrixModel/AxisHeaderDropdown';

export function getRuleHtmlElementId(
  rulePublicId: Threshold['publicId'],
) {
  // todo: add threshold-side
  return `rule-content-${rulePublicId}`;
}

export function getExpressionHtmlElementId(
  expressionPath: ExpressionUiEffectContext['expressionPath'],
) {
  return `expression-content-${CSS.escape(expressionPath)}`;
}

export function getMatrixValueHtmlElementId(target: MatrixFocusTarget): string {
  const { cellPosition, configPopover, axisDataroute } = target as {
    cellPosition?: { row: number; col: number };
    configPopover?: Axis;
    axisDataroute?: Axis;
  };

  if (cellPosition) {
    return `matrix-cell-${cellPosition.row}-${cellPosition.col}`;
  }
  if (configPopover) {
    return `matrix-config-popover-${configPopover}-axis`;
  }
  if (axisDataroute) {
    return `matrix-value-dataroute-${axisDataroute}-axis`;
  }

  return '';
}

export function getMLAHtmlElementId(
  mlaElement: (
    // attribute token of the mla attribute
    Threshold['left']['attributeToken']
    | Threshold['right']['attributeToken']
    | DataRoute['attribute']['token']
    // or the filters dropdown element
    | 'filters'
  ),
) {
  return `mla-content-${mlaElement}`;
}

export function getUiEffectKey(
  historyEntry: WorkflowHistoryEntry,
  historyAction: HistoryAction,
): UiEffectRegistryKey {
  if (historyAction === historyActions.undo) {
    // When we undo a "remove" operation - it is akin to an "add" operation
    // and vice versa for "add" and "remove" operations
    const operation = undoUiEffectMap[historyEntry.operation];
    return `${operation}:${historyEntry.uiEffectContext.target}`;
  }

  return `${historyEntry.operation}:${historyEntry.uiEffectContext.target}`;
}

/**
 * Runs the handler registered for `key` with `ctx`. The generic `K` ties the
 * looked-up handler to the context type, so the invocation type-checks without
 * a cast (a correlated-union lookup).
 */
export function runUiEffect<K extends UiEffectRegistryKey>(
  uiEffectRegistry: UiEffectRegistry,
  key: K,
  ctx: UiEffectContextForKey<K>,
): void {
  uiEffectRegistry[key]?.(ctx);
}
